import HandshakeIcon from '@mui/icons-material/Handshake'
import HubIcon from '@mui/icons-material/Hub'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { Box, Grid, Typography } from '@mui/material'
import { FC } from 'react'
import { useRecordContext, useTranslate } from 'react-admin'
import EditSamenwerkingDialog from './EditSamenwerkingDialog'
import TransferSamenwerkingDialog from './TransferSamenwerkingDialog'
import SamenwerkingStatusChip from '../../components/cards/SamenwerkingStatusChip'
import CustomTextfield from '../../components/fields/CustomTextfield'
import { Samenwerking } from '../../types/apiRequests'
import { getCaseUrl, KENMERK_SYSTEEM } from '../../utils/getCaseUrl'
import { isZakenOrganization, useOrganizationSettings } from '../../utils/useOrganizationSettings'
import DocumentsPanel from './Collaborations.details.documents'
import PartnersPanel from './Collaborations.details.partners'

const CollaborationsDetails: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<Samenwerking>()
  const orgSettings = useOrganizationSettings()
  const isZaken = isZakenOrganization(orgSettings)

  return (
    <Grid container spacing={3}>
      <Grid item md={12} lg={6}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 6 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <HubIcon sx={{ mr: '10px' }} fontSize='small'/>
              {translate('resources.collaborations.title')}
            </Typography>
            {record && orgSettings?.oin_number && record.aangemaaktDoor === orgSettings.oin_number && (
              <>
                <EditSamenwerkingDialog
                  record={record}
                  disabled={record.status !== 'OPEN'}
                />
                <TransferSamenwerkingDialog
                  record={record}
                  disabled={record.status !== 'OPEN'}
                />
              </>
            )}
          </Box>

          <CustomTextfield label="data.title" value={record?.titel} />
          <CustomTextfield label="data.descriptionCooperation" value={record?.beschrijving} />
          <CustomTextfield label="data.purpose" value={record?.samenwerkDoel} />
          <CustomTextfield label="data.id" value={record?.id} />
          <CustomTextfield label="data.status" element={<SamenwerkingStatusChip status={record?.status} />} />
          <Typography variant="body1" sx={{ mt: 4, fontWeight: 500 }} gutterBottom>
            {translate('resources.collaborations.contactPersonCompetentAuthority')}
          </Typography>
          <CustomTextfield label="data.name" value={record?.contactpersoonNaam} />
          <CustomTextfield label="data.phone" value={record?.contactpersoonTelefoonnummer} />
          <CustomTextfield label="data.email" value={record?.contactpersoonEmailadres} />

          <Typography variant="body1" sx={{ mt: 4, fontWeight: 500 }} gutterBottom>
            {translate('resources.collaborations.sourceSystem')}
          </Typography>
          <CustomTextfield label="data.name" value={record?.kenmerkSysteem} />
          <CustomTextfield
            label="data.caseNumber"
            value={record?.nummerBinnenSysteem}
            link={getCaseUrl({ system: record?.kenmerkSysteem, number: record?.nummerBinnenSysteem })}
          />

          {isZaken && record?.zaak_nummer && (
            <>
              <Typography variant="body1" sx={{ mt: 4, fontWeight: 500 }} gutterBottom>
                {translate('data.ownSystem')}
              </Typography>
              <CustomTextfield
                label="data.caseNumber"
                value={String(record.zaak_nummer)}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                link={getCaseUrl({ system: KENMERK_SYSTEEM.XXLLNC_ZAKEN, number: String(record.zaak_nummer) })}
              />
            </>
          )}
        </Box>
      </Grid>
      <Grid item md={12} lg={3}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Typography variant="h6" gutterBottom>
            <HandshakeIcon sx={{ mr: '10px' }} fontSize='small'/>
            {translate('show.partners')}
          </Typography>
          <PartnersPanel />
        </Box>
      </Grid>
      <Grid item md={12} lg={3}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Typography variant="h6" gutterBottom>
            <InsertDriveFileIcon sx={{ mr: '10px' }} fontSize='small'/>
            {translate('show.documents')}
          </Typography>
          <DocumentsPanel />
        </Box>
      </Grid>
    </Grid>
  )
}

export default CollaborationsDetails
